<template>
  <div :id="id" class="chapter-section panel">
    <p class="panel-heading">{{ $t(`chapter5.title`) }}</p>

    <fieldset class="box">
      <legend>{{ $t(`chapter5.q0.title`) }}</legend>
      <b-field :label="$t('chapter5.q0.statistisch')">
        <div id="c5q0">
          <div>
            <b-checkbox
              native-value="statistischIntern"
              v-model="study.datenverarbeitung.weiterverarbeitung"
              @change="onWeiterverarbeitungChange"
            >
              {{ $t(`chapter5.q0.intern`) }}
            </b-checkbox>
          </div>
          <div>
            <b-checkbox
              native-value="statistischExtern"
              v-model="study.datenverarbeitung.weiterverarbeitung"
              @change="save"
            >
              {{ $t(`chapter5.q0.extern`) }}
            </b-checkbox>
          </div>
          <div>
            <b-checkbox
              native-value="statistischDezentral"
              v-model="study.datenverarbeitung.weiterverarbeitung"
              @change="save"
            >
              {{ $t(`chapter5.q0.dezentral`) }}
            </b-checkbox>
          </div>
        </div>
      </b-field>

      <b-field :label="$t('chapter5.q0.sonstig')">
        <div id="c5q0">
          <div>
            <b-checkbox
              native-value="sonstigIntern"
              v-model="study.datenverarbeitung.weiterverarbeitung"
              @change="onWeiterverarbeitungChange"
            >
              {{ $t(`chapter5.q0.intern`) }}
            </b-checkbox>
          </div>
          <div>
            <b-checkbox
              native-value="sonstigExtern"
              v-model="study.datenverarbeitung.weiterverarbeitung"
              @change="save"
            >
              {{ $t(`chapter5.q0.extern`) }}
            </b-checkbox>
          </div>
          <div>
            <b-checkbox
              native-value="sonstigDezentral"
              v-model="study.datenverarbeitung.weiterverarbeitung"
              @change="save"
            >
              {{ $t(`chapter5.q0.dezentral`) }}
            </b-checkbox>
          </div>
        </div>
      </b-field>
    </fieldset>

    <fieldset v-if="showVerarbeitungsMethodenUndProgramme" class="box">
      <legend>{{ $t(`chapter5.q0a.title`) }}</legend>
      <b-field :label="$t(`chapter5.q0a.text`)">
        <b-input
          type="textarea"
          v-model="study.datenverarbeitung.verarbeitungsMethodenUndProgramme"
          @blur="save"
        />
      </b-field>
    </fieldset>

    <fieldset class="box">
      <legend>
        {{ $t(`chapter5.q1.title`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c5q1 }"
            @click.native="showInfo_c5q1 = !showInfo_c5q1"
          />
        </b-tooltip>
      </legend>

      <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c5q1">
        {{ $t('chapter5.q1.info') }}
      </InfoText>

      <b-field class="is-flex is-justify-content-flex-start">
        <b-switch v-model="showKi" type="is-success">
          {{ $t(`chapter5.q1.textCheckbox`) }}
        </b-switch>
      </b-field>

      <div class="box" v-if="showKi">
        <AbweichungStandardprozess>
          {{ $t('general.abweichungStandardprozess') }}
        </AbweichungStandardprozess>
        <b-field
          :label="$t(`chapter5.q1.text`)"
          :type="
            store.getFieldError('datenverarbeitung', 'verarbeitungMitKi.name') ? 'is-danger' : ''
          "
          :message="store.getFieldError('datenverarbeitung', 'verarbeitungMitKi.name')"
        >
          <b-input
            :required="showKi"
            v-model="study.datenverarbeitung.verarbeitungMitKi!.name"
            @blur="save"
          />
        </b-field>

        <b-field
          :label="$t(`chapter5.q1.ort`)"
          :type="
            store.getFieldError('datenverarbeitung', 'verarbeitungMitKi.ort') ? 'is-danger' : ''
          "
          :message="store.getFieldError('datenverarbeitung', 'verarbeitungMitKi.ort')"
        >
          <div v-for="option in kiOrtOptions" :key="option">
            <b-radio
              :required="showKi"
              :native-value="option"
              v-model="study.datenverarbeitung.verarbeitungMitKi!.ort"
              @change="save"
            >
              {{ $t(`chapter5.q1.${option}`) }}
            </b-radio>
            <b-field
              v-if="'cloud' === study.datenverarbeitung.verarbeitungMitKi!.ort && option === 'cloud'"
              :label="$t(`chapter5.q1.text2`)"
            >
              <b-select
                :placeholder="$t(`general.select`)"
                v-model="study.datenverarbeitung.verarbeitungMitKi!.ortCloudSitz"
                @change="save"
              >
                <option v-for="option in cloudSitzOptions" :value="option" :key="option">
                  {{ $t(`chapter5.q1.${option}`) }}
                </option>
              </b-select>
            </b-field>
          </div>
        </b-field>

        <!-- KI Training -->
        <b-field class="is-flex is-justify-content-flex-start">
          <b-checkbox
            v-model="study.datenverarbeitung.verarbeitungMitKi!.training"
            type="is-success"
            @change="save"
          >
            {{ $t(`chapter5.q1b.checkboxText`) }}
          </b-checkbox>
        </b-field>
      </div>
    </fieldset>

    <fieldset class="box">
      <legend>
        {{ $t(`chapter5.q2.title`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c5q2 }"
            @click.native="showInfo_c5q2 = !showInfo_c5q2"
          />
        </b-tooltip>
      </legend>

      <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c5q2">
        {{ $t('chapter5.q2.info') }}
        <br />
        {{ $t('chapter5.q2.infoDigaLink') }}
        <a href="https://diga.bfarm.de/de/verzeichnis" target="_blank" rel="noopener">
          <b-icon icon="open-in-new" size="is-small" />
          https://diga.bfarm.de/de/verzeichnis
        </a>
      </InfoText>

      <b-field class="is-flex is-justify-content-flex-start">
        <b-switch v-model="showDiga" type="is-success">
          {{ $t(`chapter5.q2.textCheckbox`) }}
        </b-switch>
      </b-field>

      <div class="box" v-if="showDiga">
        <b-field
          :label="$t(`chapter5.q2.text`)"
          :type="store.getFieldError('datenverarbeitung', 'diga.name') ? 'is-danger' : ''"
          :message="store.getFieldError('datenverarbeitung', 'diga.name')"
        >
          <b-input :required="showDiga" v-model="study.datenverarbeitung.diga!.name" @blur="save" />
        </b-field>
        <b-field class="is-flex is-justify-content-flex-start">
          <b-checkbox
            v-model="study.datenverarbeitung.diga!.bfarmZulassung"
            type="is-success"
            @change="save"
          >
            {{ $t(`chapter5.q2.zulassungCheckbox`) }}
          </b-checkbox>
        </b-field>

        <b-field
          class="is-flex is-justify-content-flex-start"
          v-if="!study.studienparameter.eigeninitiiert"
        >
          <b-checkbox
            v-model="study.datenverarbeitung.diga!.durchSponsorVorgegeben"
            type="is-success"
            @change="save"
          >
            {{ $t(`chapter5.q2.sponsorCheckbox`) }}
          </b-checkbox>
        </b-field>

        <AbweichungStandardprozess v-if="showDigaWarning">
          {{ $t('general.abweichungStandardprozess') }}
        </AbweichungStandardprozess>
      </div>
    </fieldset>

    <fieldset class="box">
      <legend>{{ $t(`chapter5.q3.title`) }}</legend>
      <b-field class="is-flex is-justify-content-flex-start">
        <b-switch
          v-model="showBildgebendeVerfahren"
          type="is-success"
          :required="showBildgebendeVerfahren"
        >
          {{ $t(`chapter5.q3.textCheckbox`) }}
        </b-switch>
      </b-field>

      <fieldset class="box" v-if="showBildgebendeVerfahren">
        <b-field
          :label="$t(`chapter5.q3.text`)"
          :type="
            store.getFieldError('datenverarbeitung', 'bildgebendeVerfahren.speicherOrt')
              ? 'is-danger'
              : ''
          "
          :message="store.getFieldError('datenverarbeitung', 'bildgebendeVerfahren.speicherOrt')"
        >
          <div v-for="option in bildgebendeVerfahrenOptions" :key="option">
            <b-radio
              :required="true"
              type="is-success"
              :native-value="option"
              v-model="study.datenverarbeitung.bildgebendeVerfahren!.speicherOrt"
              @change="save"
            >
              {{ $t(`chapter5.q3.${option}`) }}
            </b-radio>
          </div>
        </b-field>
      </fieldset>
      <b-field class="is-flex is-justify-content-flex-start" v-if="showBildgebendeVerfahren">
        <b-checkbox
          v-model="study.datenverarbeitung.bildgebendeVerfahren!.zurVeroeffentlichung"
          type="is-success"
          @change="save"
        >
          {{ $t(`chapter5.q3.zurVeroeffentlichung`) }}
        </b-checkbox>
      </b-field>
    </fieldset>

    <fieldset class="box">
      <legend>
        {{ $t(`chapter5.q4.title`) }}
        <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
          <b-icon
            type="is-info"
            icon="information-variant"
            class="info-toggle"
            :class="{ 'is-active': showInfo_c5q4 }"
            @click.native="showInfo_c5q4 = !showInfo_c5q4"
          />
        </b-tooltip>
      </legend>

      <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c5q4">
        {{ $t('chapter5.q4.info') }}
      </InfoText>

      <b-field class="is-flex is-justify-content-flex-start">
        <b-checkbox
          v-model="study.datenverarbeitung.publikationPersonenbezogenerDaten"
          type="is-success"
          @change="onPublikationPersonenbezogenerDatenChange"
        >
          {{ $t(`chapter5.q4.text`) }}
        </b-checkbox>
      </b-field>
      <b-field
        :label="$t(`chapter5.q4.beschreibung`)"
        v-if="study.datenverarbeitung.publikationPersonenbezogenerDaten"
        :type="
          store.getFieldError('datenverarbeitung', 'publikationPersonenbezogenerDatenBeschreibung')
            ? 'is-danger'
            : ''
        "
        :message="
          store.getFieldError('datenverarbeitung', 'publikationPersonenbezogenerDatenBeschreibung')
        "
      >
        <b-input
          :required="study.datenverarbeitung.publikationPersonenbezogenerDaten"
          v-model="study.datenverarbeitung.publikationPersonenbezogenerDatenBeschreibung"
          @blur="save"
        />
      </b-field>
    </fieldset>

    <div class="box">
      <b-field
        :type="store.getFieldError('datenverarbeitung', 'datenfluss') ? 'is-danger' : ''"
        :message="store.getFieldError('datenverarbeitung', 'datenfluss')"
      >
        <template #label>
          {{ $t(`chapter5.q5.title`) }}
          <b-tooltip :label="$t(`general.showInfo`)" position="is-right" type="is-info">
            <b-icon
              type="is-info"
              icon="information-variant"
              class="info-toggle"
              :class="{ 'is-active': showInfo_c5q5 }"
              @click.native="showInfo_c5q5 = !showInfo_c5q5"
            />
          </b-tooltip>
        </template>

        <b-input
          :required="true"
          type="textarea"
          v-model="study.datenverarbeitung.datenfluss"
          @blur="save"
        />
      </b-field>
      <InfoText :button-label="$t('general.showInfo')" v-if="showInfo_c5q5">
        {{ $t('chapter5.q5.info') }}
      </InfoText>
    </div>
  </div>
</template>

<script setup lang="ts">
import { computed } from 'vue'
import { useStudyStore } from '@/stores/studyStore'
import { useInfoToggle } from '@/composables/useVisibilityToggles'
import InfoText from '@/components/InfoText.vue'
import { BCheckbox, BField, BIcon, BInput, BRadio, BSelect, BSwitch, BTooltip } from 'buefy'
import AbweichungStandardprozess from '@/components/AbweichungStandardprozess.vue'

defineProps<{ id: string; modelValue?: object }>()

const store = useStudyStore()
const study = store.study
const title = 'Datenverarbeitung'

const kiOrtOptions = ['cloud', 'onPremise']
const cloudSitzOptions = ['forschungsPartner', 'euDe', 'ausserhalbEuropa']
const bildgebendeVerfahrenOptions = ['selbstGespeichert', 'imVorliegendenSystemGepeichert']

const save = () => {
  store.updateChapter('datenverarbeitung', study.datenverarbeitung)
}

const showVerarbeitungsMethodenUndProgramme = computed(
  () =>
    study.datenverarbeitung.weiterverarbeitung.includes('statistischIntern') ||
    study.datenverarbeitung.weiterverarbeitung.includes('sonstigIntern'),
)

const showInfo_c5q1 = useInfoToggle()
const showInfo_c5q2 = useInfoToggle()
const showInfo_c5q4 = useInfoToggle()
const showInfo_c5q5 = useInfoToggle()

// showKi/showDiga/showBildgebendeVerfahren getters are literally the
// !== undefined check, so the template's non-null assertions on the
// corresponding fields hold whenever their v-if/v-model guard is true.
const initKi = () => {
  study.datenverarbeitung.verarbeitungMitKi = {
    name: '',
    ort: undefined,
    ortCloudSitz: undefined,
    training: false,
  }
}
const clearKi = () => {
  study.datenverarbeitung.verarbeitungMitKi = undefined
}
const showKi = computed({
  get: () => study.datenverarbeitung.verarbeitungMitKi !== undefined,
  set: (value: boolean) => (value ? initKi() : clearKi()),
})

const initDiga = () => {
  study.datenverarbeitung.diga = { name: '', bfarmZulassung: false, durchSponsorVorgegeben: false }
}
const clearDiga = () => {
  study.datenverarbeitung.diga = undefined
}
const showDiga = computed({
  get: () => study.datenverarbeitung.diga !== undefined,
  set: (value: boolean) => (value ? initDiga() : clearDiga()),
})

const showDigaWarning = computed(() => {
  if (study.datenverarbeitung.diga === undefined) {
    return false
  }
  return study.studienparameter.eigeninitiiert
    ? !study.datenverarbeitung.diga.bfarmZulassung
    : !(
        study.datenverarbeitung.diga.bfarmZulassung ||
        study.datenverarbeitung.diga.durchSponsorVorgegeben
      )
})

const initBildgebendeVerfahren = () => {
  study.datenverarbeitung.bildgebendeVerfahren = {
    speicherOrt: 'selbstGespeichert',
    zurVeroeffentlichung: false,
  }
}
const clearBildgebendeVerfahren = () => {
  study.datenverarbeitung.bildgebendeVerfahren = undefined
}
const showBildgebendeVerfahren = computed({
  get: () => study.datenverarbeitung.bildgebendeVerfahren !== undefined,
  set: (value: boolean) => (value ? initBildgebendeVerfahren() : clearBildgebendeVerfahren()),
})

function onWeiterverarbeitungChange() {
  if (
    !(study.datenverarbeitung.weiterverarbeitung || []).includes('sonstigIntern') &&
    !(study.datenverarbeitung.weiterverarbeitung || []).includes('statistischIntern')
  ) {
    study.datenverarbeitung.verarbeitungsMethodenUndProgramme = undefined
  }
  save()
}

function onPublikationPersonenbezogenerDatenChange() {
  if (study.datenverarbeitung.publikationPersonenbezogenerDaten !== true) {
    study.datenverarbeitung.publikationPersonenbezogenerDatenBeschreibung = undefined
  }
  save()
}
</script>

<style scoped>
#c5q0 {
  margin-left: 1rem;
}
</style>
